import Button from '../../Buttons/Button';
import AddressForm from '../../ProfilePanels/AddressPanel/AddressForm';
import Icon from '../../Icon';
import useAddressForm from '@/hooks/addressForm';
import { useGetUserAddressesQuery } from '@/redux/api/client/userAddresses';
import { useEffect, useMemo, useState } from 'react';
import Shimmer from '../../Shimmer';
import {
  getFormattedAddressDescription,
  getInlineFormattedAddress
} from '../../ProfileSections/AddressesSection/address.utils';
import { Address } from '@/@types/client-api';
import classnames from 'classnames';
import AddressPanel from '../../ProfilePanels/AddressPanel';
import { Form } from '@radix-ui/react-form';

type CheckoutAddressPanelProps = {
  isShippingRequired?: boolean;
  selectedAddress?: Address;
  isLoadingCheckout: boolean;
  type: 'billing' | 'shipping';
  setAddressSk: (sk: number) => void;
  refetchCheckout: () => void;
};

const CheckoutAddressPanel: React.FC<CheckoutAddressPanelProps> = ({
  isShippingRequired,
  selectedAddress,
  isLoadingCheckout,
  type,
  refetchCheckout,
  setAddressSk
}) => {
  const {
    data: userAddresses,
    isLoading: isLoadingAddresses,
    refetch: refetchAddresses
  } = useGetUserAddressesQuery();
  const [selectedAddressSk, setSelectedAddressSk] = useState(-1);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isAddingNewAddress, setIsAddingNewAddress] = useState(false);
  const [isNewAddressAdded, setIsNewAddressAdded] = useState(false);
  const isCollapsed = !selectedAddress && isShippingRequired && type === 'billing';

  const addressProps = useAddressForm(
    userAddresses?.addressCategoryLookup ?? [],
    userAddresses?.addressStateLookup ?? [],
    userAddresses?.addressCountryLookup ?? [],
    refetchCheckout,
    refetchAddresses
  );

  const handleOnEditAddress = () => {
    setIsEditingAddress(true);
  };

  const handleUpdateAddress = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setAddressSk(selectedAddressSk);
    refetchCheckout();
    setIsEditingAddress(false);
  };

  const handleSelectAddress = (sk: number) => {
    setSelectedAddressSk(sk);
  };

  const handleAddNewAddress = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsAddingNewAddress(true);
  };

  const billingAddressInfo = useMemo(() => {
    if (selectedAddress) {
      const country = userAddresses?.addressCountryLookup?.find(
        item => item.pk === selectedAddress.country
      );

      return getFormattedAddressDescription(selectedAddress, country?.countryName);
    }
  }, [selectedAddress, userAddresses?.addressCountryLookup]);

  const addressSelector = useMemo(() => {
    return userAddresses?.items
      ?.filter(x => (x.sk ?? 0) > 0)
      ?.sort(x => (x.isPrimaryBAddress || x.isPrimarySAddress ? -1 : 1))
      .map(x => {
        const country = userAddresses?.addressCountryLookup?.find(item => item.pk === x.country);
        const isChecked = selectedAddressSk === x.sk;

        return (
          <div
            role="radio"
            key={x.sk}
            onClick={() => handleSelectAddress(x.sk ?? -1)}
            className={classnames(
              'text-2 flex w-full cursor-pointer justify-between rounded-[4px] border border-solid p-[24px] hover:border-black',
              isChecked ? 'border-black bg-gray-feather' : 'border-gray-light'
            )}
          >
            <div className="flex flex-row gap-2">
              <input
                id={`address-radio-${x.sk}`}
                name="address-radio"
                tabIndex={isChecked ? 0 : -1}
                type="radio"
                checked={isChecked}
                aria-checked={isChecked}
                onChange={() => {}}
                className="mt-px aspect-square size-[14px] min-h-[14px] min-w-[14px] cursor-pointer rounded-full accent-black lg:mt-[2px] lg:size-4 lg:min-h-4 lg:min-w-4"
              />
              <label className="flex flex-col" htmlFor={`address-radio-${x.sk}`}>
                <span className="text-2-medium">{x.attention}</span>
                <span color="text-gray-dark">
                  {getInlineFormattedAddress(x, country?.countryName)}
                </span>
              </label>
            </div>
            <div className="text-2-medium text-gray-dark">
              {(type === 'billing' && x.isPrimaryBAddress) ||
              (type === 'shipping' && x.isPrimarySAddress)
                ? 'Default'
                : ''}
            </div>
          </div>
        );
      });
  }, [selectedAddressSk, type, userAddresses?.addressCountryLookup, userAddresses?.items]);

  useEffect(() => {
    if (selectedAddress) {
      setSelectedAddressSk(selectedAddress.sk ?? -1);
    }
  }, [selectedAddress]);

  useEffect(() => {
    if (isNewAddressAdded && userAddresses) {
      const addresses = userAddresses?.items?.filter(x => (x.sk ?? 0) > 0);
      const newAddressSk = addresses?.[addresses.length - 1].sk;
      if (newAddressSk) {
        setSelectedAddressSk(newAddressSk);
        setIsNewAddressAdded(false);
      }
    }
  }, [isNewAddressAdded, selectedAddress, userAddresses, userAddresses?.items]);

  const renderCheckIcon = (
    <div className="flex size-[24px] items-center justify-center rounded-full bg-black">
      <Icon size="medium" name="checkmark" className="text-white" />
    </div>
  );

  return (
    <section className="flex flex-col gap-[32px] rounded-[4px] bg-white px-[32px] py-[40px]">
      <div className="flex justify-between">
        <div className="flex flex-row gap-2">
          {selectedAddress && !isEditingAddress && renderCheckIcon}
          <h2 id="address-panel" className="heading-6-medium">
            {type === 'billing' ? 'Billing Address' : 'Shipping Address'}
          </h2>
        </div>
        {!isEditingAddress && !isLoadingCheckout && !isCollapsed && selectedAddress && (
          <button className="text-2 underline hover:text-red" onClick={handleOnEditAddress}>
            Edit
          </button>
        )}
      </div>
      {isLoadingCheckout ? (
        <Shimmer className="h-20 w-full" />
      ) : isCollapsed ? (
        <></>
      ) : (
        <>
          {isEditingAddress ? (
            <>
              <Form className="flex flex-col gap-[16px]">
                <div
                  className="flex flex-col gap-[16px]"
                  role="radiogroup"
                  aria-labelledby="address-panel"
                >
                  {addressSelector}
                </div>
                <button
                  className="text-2-bold mb-[16px] flex flex-row gap-2 self-start hover:text-red disabled:text-gray-dark disabled:hover:text-gray-dark"
                  onClick={e => handleAddNewAddress(e)}
                >
                  <span>Add Address</span>
                  <Icon name="add" size="small" />
                </button>
                <Button
                  type="submit"
                  loading={isLoadingAddresses}
                  disabled={isLoadingAddresses}
                  onClick={handleUpdateAddress}
                  color="black"
                  label={`Save ${type} address`}
                  className="text-1 w-full py-[25px] font-bold"
                />
              </Form>
              <AddressPanel
                isInitialAddress={!userAddresses?.items?.length}
                isOpen={isAddingNewAddress}
                onOpenChange={setIsAddingNewAddress}
                refetchAddresses={refetchAddresses}
                setIsNewAddressAdded={setIsNewAddressAdded}
                addressCategoryLookup={userAddresses?.addressCategoryLookup ?? []}
                addressStateLookup={userAddresses?.addressStateLookup ?? []}
                addressCountryLookup={userAddresses?.addressCountryLookup ?? []}
                triggerButton={<></>}
                type={type}
              />
            </>
          ) : (
            <>
              {!selectedAddress ? (
                <AddressForm isInlineForm={true} isInitialAddress={true} {...addressProps} />
              ) : (
                <div className="text-1 block overflow-hidden text-ellipsis whitespace-pre-wrap">
                  {billingAddressInfo}
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default CheckoutAddressPanel;
